'use client';
import React, { useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { CarouselContextProps, useCarousel } from '@/components/common/carousel';

import styles from './styles.module.scss';
import Indicator from '@/components/complex/indicator';

export interface ImagePaginationProps {
  /**
   * Size of the pagination indicators (default: 8)
   */
  size?: number;
  /**
   * Color of inactive indicators (default: '#D1D5DB')
   */
  inactiveColor?: string;
  /**
   * Color of active indicator (default: '#3B82F6')
   */
  activeColor?: string;
}

/**
 * A component that displays pagination indicators for images
 * Shows circles as outlines for the number of images, with the active page shown as a solid circle
 * Includes left/right arrow buttons for navigation
 */
export const ImagePagination = ({
                                  size = 8,
                                  activeColor = '#3B82F6',
                                }: ImagePaginationProps) => {
  const carousel = useCarousel();

  const handlePrevPage = useCallback(() => {
    if (carousel.index > 0) {
      const newIndex = carousel.index - 1;
      carousel.setIndex(newIndex);
    }
  }, [carousel]);

  const handleNextPage = useCallback(() => {
    if (carousel.index < size - 1) {
      const newIndex = carousel.index + 1;
      carousel.setIndex(newIndex);
    }
  }, [carousel]);

  const isFirstPage = carousel.index === 0;
  const isLastPage = carousel.index === size - 1;

  if (size <= 1) return null;

  return (
    <div className={styles.root}>
      <div className='cursor-pointer' onClick={handlePrevPage}>
        <ChevronLeft
          size={20}
          color={activeColor}
          style={{
            opacity: isFirstPage ? 0 : 0.7,
            //@ts-ignore
            transition: 'all 0.3s ease',
          }}
        />
      </div>

      <Indicator size={size} index={carousel.index} />

      <div className='cursor-pointer' onClick={handleNextPage}>
        <ChevronRight
          size={20}
          color={activeColor}
          style={{
            opacity: isLastPage ? 0 : 0.7,
            //@ts-ignore
            transition: 'all 0.3s ease',
          }}
        />
      </div>
    </div>
  );
};

export default ImagePagination;
